import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MyMembershipDto, Workspace } from '@up2stay/up2promo-api-client';
import { WorkspaceMembersApi } from '@up2stay/up2promo-api-client';
import { useCallback } from 'react';

import { useAuthStore } from '@/core/store/auth.store';

export const membershipsQueryKey = (userId?: string) =>
  ['memberships', userId] as const;

export function workspacesFromMemberships(
  memberships: MyMembershipDto[],
): Workspace[] {
  if (!Array.isArray(memberships)) {
    return [];
  }

  return memberships
    .filter((membership) => membership.status === 'active')
    .map((membership) => membership.workspace)
    .filter((ws): ws is Workspace => ws !== undefined);
}

/** Merge a workspace PATCH into the memberships cache (current workspace is derived from it). */
export function patchWorkspaceInMemberships(
  memberships: MyMembershipDto[],
  workspace: Workspace,
): MyMembershipDto[] {
  return memberships.map((membership) => {
    if (membership.workspace.id !== workspace.id) {
      return membership;
    }
    return {
      ...membership,
      workspace: { ...membership.workspace, ...workspace },
    };
  });
}

export function usePatchWorkspaceInMemberships() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id);

  return useCallback(
    (workspace: Workspace) => {
      queryClient.setQueryData(
        membershipsQueryKey(userId),
        (prev: MyMembershipDto[] | undefined) =>
          prev ? patchWorkspaceInMemberships(prev, workspace) : prev,
      );
    },
    [queryClient, userId],
  );
}

export function useMembershipsQuery() {
  const user = useAuthStore((state) => state.user);
  const hydrated = useAuthStore((state) => state.hydrated);

  return useQuery({
    queryKey: membershipsQueryKey(user?.id),
    queryFn: async () => {
      const response = await WorkspaceMembersApi.getMyMemberships();
      return response.data ?? [];
    },
    enabled: hydrated && !!user?.id,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
